import {
	CloseOutlined,
	ExportOutlined,
	InboxOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import {
	Button,
	Col,
	Divider,
	Form,
	Modal,
	Row,
	Switch,
	Table,
	Tag,
	Tooltip,
	Upload,
	UploadFile,
} from 'antd';
import Title from 'antd/es/typography/Title';
import { UploadChangeParam } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { payrollApi } from 'Api/payroll';
import { viewPayrollApi } from 'Api/payrollView';
import Buttons from 'components/Global/Buttons';
import InputNumberField from 'components/Global/InputNumberField';
import SelectDropdown from 'components/Global/SelectDropdown';
import TextareaFieldInput from 'components/Global/textareaField';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { DownloadSvg } from 'utils/svgs';
import {
	capitalizeWords,
	formatNumber,
	hasFormError,
	invalidText,
	toastText,
	validateFormData,
} from 'utils/utils';
import GlobalHeader from '../../GlobalHeader';
import styles from './index.module.scss';
import './index.scss';

interface Props {
	selectedPayrollId: string;
	onlyView: boolean;
	setIsUniversalLoading: any;
	headerData: any;
	payPeriodId: any;
	year: number;
	showSteps: boolean;
}

interface ColumnData {
	parent: string;
	title: string;
	children?: ColumnData[];
	key?: string;
}

const TotalDeductionTable: React.FC<Props> = ({
	selectedPayrollId,
	onlyView,
	setIsUniversalLoading,
	headerData,
	payPeriodId,
	year,
	showSteps,
}) => {
	const [showEmployer, setShowEmployer] = useState(false);
	const [deductionData, setDeductionData] = useState<any[]>([]);
	const [rowData, setRowData] = useState<any[]>([]);
	const [columnsData, setColumnsData] = useState<ColumnData[]>([]);
	const [isDeductionLoading, setIsDeductionLoading] = useState(false);
	const [deductionLimit, setDeductionLimit] = useState(0);
	const [isThirdPartyLoan, setIsThirdPartyLoan] = useState<boolean>(false);

	const [payrollSpecificData, setPayrollSpecificData] = useState<any>({});
	// Modal states
	const [isAddDeductionModalVisible, setIsAddDeductionModalVisible] =
		useState(false);
	const [isUploadLoanModalVisible, setIsUploadLoanModalVisible] =
		useState(false);

	const [deductionDetails, setDeductionDetails] = useState<any>({
		employeeId: '',
		amount: null,
		notes: '',
		deductionType: '',
	});
	const [deductionsOptions, setDeductionsOptions] = useState<any>({});

	const [formError, setFormError] = useState<any>({
		employeeId: false,
		amount: false,
		notes: false,
		deductionType: false,
	});

	const [isLoading, setIsLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [totalRecords, setTotalRecords] = useState(0);
	const [pageSize, setPageSize] = useState(31);
	const [file, setFile] = useState<any>(null);
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [employeeOptions, setEmployeeOptions] = useState<any[]>([]);
	let isRemoving = false;
	const [isUploadLoading, setIsUploadLoading] = useState(false);

	const [importResults, setImportResults] = useState<any>(null);
	const [showResults, setShowResults] = useState(false);

	// Download template function
	const downloadTemplate = async () => {
		try {
			const csvContent = [
				'WW Deduction Code,NRC,Current Balance,Installment',
				'LOAN001,123456/78/9,50000.00,2500.00',
				'LOAN002,987654/32/1,25000.50,1250.25',
				'PERSONAL_LOAN,456789/12/3,15000.00,750.00',
			].join('\n');

			const blob = new Blob([csvContent], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = 'third_party_loan_template.csv';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			toastText('Template downloaded successfully', 'success');
		} catch (error) {
			
			toastText('Failed to download template', 'error');
		}
	};

	const handleUpload = async (file: any) => {
		const reader = new FileReader();

		reader.onload = async (e: any) => {
			try {
				setIsUploadLoading(true);

				// ✅ 1. Parse CSV headers
				const csvText = e.target.result;
				const [headerLine] = csvText.split('\n');
				const uploadedColumns = headerLine
					.split(',')
					.map((col: any) => col.trim().replace(/^"(.*)"$/, '$1'));

				// ✅ 2. Define expected columns
				const jsonData = {
					'WW Deduction Code': '27',
					NRC: '000000/00/0',
					'Current Balance': '2000',
					Installment: '4000',
				};

				const requiredColumns = Object.keys(jsonData);

				// ✅ 3. Validate headers
				const missingColumns = requiredColumns.filter(
					(col) => !uploadedColumns.includes(col)
				);

				if (missingColumns.length > 0) {
					let message =
						'Please Download the CSV Template to ensure correct format:\n';

					if (missingColumns.length > 0) {
						const displayMissing = missingColumns.slice(0, 3);
						message += `Missing: ${displayMissing.join(', ')}`;
						if (missingColumns.length > 3) {
							message += ` and ${missingColumns.length - 3} more`;
						}
					}

					toastText(message, 'error');
					setIsUploadLoading(false);
					setFileList([]);
					return;
				}

				// ✅ 4. Only proceed if columns match
				let formData = new FormData();

				formData.append('documentName', 'third_party_loan');
				formData.append('file', file);
				formData.append('payrollId', selectedPayrollId);

				const validatedData: any = await payrollApi.uploadLoanCsvFile(
					formData
				);
				const apiResponseData = validatedData.data.data;
				
				setImportResults(apiResponseData);
				setShowResults(true);
			} catch (error: any) {
				
				let message =
					error?.response?.data?.message ||
					`Failed to process file. Please try again`;
				toastText(message, 'error');
	
				setFileList([]);
				toastText(message, 'error');
			} finally {
				setIsUploadLoading(false);
			}
		};

		reader.onerror = () => {
			toastText('File reading failed', 'error');
		};

		reader.readAsText(file);

		// Prevent default upload
		return false;
	};

	const resetImport = () => {
		setImportResults(null);
		setShowResults(false);
		setFile(null);
		setFileList([]);
	};

	// Function to close and refresh
	const closeAndRefresh = () => {
		if (importResults?.summary?.successfulRows > 0) {
			// Refresh the deductions table
			fetchDeductions(selectedPayrollId);
		}
		cancelUploadRecordModal();
	};

	const renderModalContent = () => {
		if (showResults && importResults) {
			const { summary, errors } = importResults;
			const hasSuccess = summary.successfulRows > 0;
			const hasErrors = summary.errorRows > 0;

			return (
				<div className={`${styles['modal']} modal`}>
					<div className={styles['modal-header']}>
						<Title
							level={4}
							style={{ color: '#584495', margin: 0 }}
						>
							{hasSuccess && !hasErrors && '🎉 Import Successful'}
							{hasSuccess &&
								hasErrors &&
								'⚠️ Import Completed with Errors'}
							{!hasSuccess && hasErrors && '❌ Import Failed'}
						</Title>
						<div
							className={styles['close-icon']}
							onClick={closeAndRefresh}
						>
							<CloseOutlined />
						</div>
					</div>

					{/* Summary Section */}
					<div
						style={{
							padding: '16px',
							backgroundColor: '#fafafa',
							borderLeft: `4px solid ${
								hasErrors ? '#ff4d4f' : '#52c41a'
							}`,
							borderRadius: '6px',
							marginBottom: '16px',
						}}
					>
						<Title
							level={5}
							style={{ margin: 0, color: '#584495' }}
						>
							Import Summary
						</Title>
						<div
							style={{
								display: 'flex',
								gap: '16px',
								marginTop: '8px',
							}}
						>
							{hasSuccess && (
								<Tag
									style={{
										background: '#f5f5f5',
										color: '#009049',
										border: 'none',
									}}
								>
									{summary.successfulRows} Imported
								</Tag>
							)}
							{hasErrors && (
								<Tag
									style={{
										background: '#f5f5f5',
										color: '#FF0000',
										border: 'none',
									}}
								>
									{summary.errorRows} Failed
								</Tag>
							)}
							<Tag
								style={{
									background: '#f5f5f5',
									color: '#584495',
									border: 'none',
								}}
							>
								{summary.totalRows} Total Processed
							</Tag>
						</div>
					</div>

					{/* Success Preview */}
					{hasSuccess && (
						<div style={{ marginBottom: '16px' }}>
							<Title
								level={5}
								style={{ color: '#009049', margin: '0 0 8px' }}
							>
								Successfully Imported ({summary.successfulRows}{' '}
								loans)
							</Title>
							<div
								style={{
									background: '#fafafa',
									border: '1px solid #f0f0f0',
									borderRadius: '6px',
									padding: '12px',
									fontSize: '14px',
								}}
							>
								{summary.successSample?.map(
									(item: any, index: number) => (
										<>
											<div
												key={index}
												style={{
													marginBottom: '6px',
													display: 'flex',
													justifyContent:
														'space-between',
												}}
											>
												<div>{item.employeeName}</div>
												<div
													style={{
														color: '#666',
														fontSize: '12px',
														marginTop: '2px',
													}}
												>
													Deduction Code:{' '}
													{item.deductionCode} |
													Amount: ZMW{' '}
													{item.installment}
												</div>
											</div>
											{index <
												summary.successSample.length -
													1 && (
												<Divider
													style={{ margin: '8px 0' }}
												/>
											)}
										</>
									)
								)}
								{summary.hasMoreSuccess && (
									<div
										style={{
											fontStyle: 'italic',
											color: '#666',
											marginTop: '8px',
											textAlign: 'center',
											fontSize: '12px',
										}}
									>
										...and {summary.successfulRows - 5} more
										loans added
									</div>
								)}
							</div>
						</div>
					)}

					{/* Errors Section */}
					{hasErrors && (
						<div style={{ marginBottom: '16px' }}>
							<Title
								level={5}
								style={{ color: '#FF0000', margin: '0 0 8px' }}
							>
								Failed Records ({summary.errorRows})
							</Title>
							<div
								style={{
									maxHeight: '200px',
									overflowY: 'auto',
									padding: '12px',
									background: '#fff',
									border: '1px solid #f0f0f0',
									borderRadius: '6px',
								}}
							>
								{errors.map((error: any, index: number) => (
									<div
										key={index}
										style={{
											marginBottom: '12px',
											padding: '8px 0',
											borderBottom:
												index < errors.length - 1
													? '1px solid #eee'
													: 'none',
										}}
									>
										<div
											style={{
												fontWeight: 'bold',
												fontSize: '13px',
												display: 'flex',
												justifyContent: 'space-between',
											}}
										>
											<span>Row {error.row}</span>
											<span
												style={{
													fontWeight: 'normal',
													color: '#666',
												}}
											>
												Deduction Code:{' '}
												{
													error.data[
														'WW Deduction Code'
													]
												}{' '}
												| NRC: {error.data.NRC}
											</span>
										</div>
										<div
											style={{
												fontSize: '12px',
												color: '#666',
												marginTop: '4px',
											}}
										>
											{error.errors.join(' • ')}
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Action Buttons */}
					<div
						style={{
							display: 'flex',
							gap: '12px',
							justifyContent: 'flex-start',
							paddingTop: '16px',
							borderTop: '1px solid #f0f0f0',
						}}
					>
						{hasErrors && (
							<Button onClick={resetImport}>
								Import Another File
							</Button>
						)}

						{hasErrors && (
							<Button
								icon={<ExportOutlined />}
								onClick={() => {
									const errorCsv = errors
										.map(
											(e: any) =>
												`"${e.row}","${
													e.data['WW Deduction Code']
												}","${
													e.data.NRC
												}","${e.errors.join('; ')}"`
										)
										.join('\n');

									const blob = new Blob(
										[
											`"Row","Deduction Code","NRC","Errors"\n${errorCsv}`,
										],
										{ type: 'text/csv' }
									);

									const url =
										window.URL.createObjectURL(blob);
									const link = document.createElement('a');
									link.href = url;
									link.download = `loan_import_errors_${
										new Date().toISOString().split('T')[0]
									}.csv`;
									link.click();
									window.URL.revokeObjectURL(url);

									toastText(
										'Error report downloaded',
										'success'
									);
								}}
							>
								Download Error Report
							</Button>
						)}

						<Button
							type="primary"
							style={{ background: '#584495' }}
							onClick={closeAndRefresh}
						>
							{hasSuccess ? 'Done' : 'Close'}
						</Button>
					</div>

					{/* Bottom Info */}
					{hasSuccess && (
						<div
							style={{
								marginTop: '12px',
								padding: '8px 12px',
								background: '#fafafa',
								border: '1px solid #f0f0f0',
								borderRadius: '6px',
								fontSize: '12px',
								color: '#584495',
								textAlign: 'center',
							}}
						>
							💡 The imported loans have been added to your
							payroll deductions.
						</div>
					)}
				</div>
			);
		}

		// Original upload UI
		return (
			<div className={`${styles['modal']} modal`}>
				<div className={styles['modal-header']}>
					<Title level={4}>Upload Loan</Title>
					<div className={styles['right-align-items']}>
						<div>
							<p
								className={styles['template-link']}
								onClick={downloadTemplate}
							>
								<DownloadSvg />
								Download Template
							</p>
						</div>
						<div
							className={styles['close-icon']}
							onClick={cancelUploadRecordModal}
						>
							<CloseOutlined />
						</div>
					</div>
				</div>
				<div>
					<div className={styles['modalUploadContainer']}>
						<Form.Item name="file">
							<Dragger {...propsUpload} className="test">
								<p className="ant-upload-drag-icon">
									<InboxOutlined />
								</p>
								<p className="ant-upload-hint">
									Drag and drop a file or choose file from
									Device Supported file formats.
									<br />
									CSV |{' '}
									<span className="color-purple">
										Maximum file size: 5MB
									</span>
								</p>
							</Dragger>
						</Form.Item>
					</div>

					<div className={styles['buttons']}>
						<Buttons buttons={uploadButtons} />
					</div>
				</div>
			</div>
		);
	};

	const handleFileSubmit = async () => {
		if (!file) {
			toastText('Please select csv file', 'error');
			return;
		}
		try {
			await handleUpload(file);
		} catch (error: any) {
			
			let message =
				error?.response?.data?.message ||
				'Something went wrong in uploading csv';
			toastText(message, 'error');
		}
	};

	const uploadButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false,
			disabled: isUploadLoading,
			onclick: () => {
				cancelUploadRecordModal();
			},
			minWidth: '10rem',
		},
		{
			text: 'Upload',
			isLoading: isUploadLoading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: isUploadLoading,
			onclick: () => {
				handleFileSubmit();
			},
			minWidth: '10rem',
		},
	];

	const propsUpload = {
		name: 'file',
		accept: '.csv',
		maxCount: 1,
		fileList: fileList,
		beforeUpload: (file: UploadFile) => {
			if (!file) {
				return;
			}

			const isCSV =
				file.type === 'text/csv' || file.name.endsWith('.csv');
			if (!isCSV) {
				toastText('Only CSV files are allowed', 'error');
				return Upload.LIST_IGNORE;
			}

			const isLt1M = file.size! / 1024 / 1024 < 5;
			if (!isLt1M) {
				toastText('File must be smaller than 5MB', 'error');
				return Upload.LIST_IGNORE;
			}

			setFile(file);
			setFileList([file]);
			return false;
		},
		onChange(info: UploadChangeParam<UploadFile>) {
			const { file } = info;
			if (!isRemoving) {
				setFile(file);
				setFileList([file]);
			} else {
				isRemoving = false;
				setFile(null);
				setFileList([]);
			}
		},
		onDrop: () => {
			setFile(null);
			setFileList([]);
		},
		onRemove: () => {
			isRemoving = true;
		},
	};

	const cancelUploadRecordModal = () => {
		setFile(null);
		setFileList([]);
		setIsUploadLoanModalVisible(false);
		setIsLoading(false);
		setImportResults(null);
		setShowResults(false);
	};

	const tableChangeHandler = (pagination: any, filters: any, sorter: any) => {
		setTotalRecords(pagination.total);
		setPage(pagination.current);
		setPageSize(pagination.pageSize);
	};

	const { data } = useSelector((state: any) => state?.userProfile);

	const { conversionRate, usdCurrencyToggle, isUsdCurrency } = useSelector(
		(state: any) => state.usdCurrencyToggle
	);

	const handleSwitchChange = (checked: boolean) => {
		setShowEmployer(checked);
	};

	// Modal handlers
	const showAddDeductionModal = () => {
		setIsAddDeductionModalVisible(true);
	};

	const showUploadLoanModal = () => {
		setIsUploadLoanModalVisible(true);
	};

	const handleAddDeductionCancel = () => {
		setIsAddDeductionModalVisible(false);
		setFormError({
			employeeId: false,
			amount: false,
			notes: false,
			deductionType: false,
		});
		setDeductionDetails({
			employeeId: '',
			amount: null,
			notes: '',
			deductionType: '',
		});
	};

	// Helper function to get proper column title
	const getColumnTitle = (key: string, title: string) => {
		switch (key) {
			case 'napsaEmployeeShare':
				return 'NAPSA Employee Share';
			case 'napsaEmployerShare':
				return 'NAPSA Employer Share';
			case 'nhimaEmployeeShare':
				return 'NHIMA Employee Share';
			case 'nhimaEmployerShare':
				return 'NHIMA Employer Share';
			default:
				return capitalizeWords(title);
		}
	};

	const formatData = (rows: any, napsaCapLimit: number) => {
		const formattedRowData = rows?.map((item: any) => {
			const formattedItem: any = {
				__raw__: item,
			};
			for (const key in item) {
				if (key === 'Employee') {
					formattedItem[key] = item[key];
				} else if (!usdCurrencyToggle && isUsdCurrency) {
					const value = Number(item[key]);
					formattedItem[key] = formatNumber(
						value,
						data?.CompanyCurrencies,
						true
					);
				} else if (
					(key === 'napsaEmployerShare' ||
						key === 'napsaEmployeeShare') &&
					usdCurrencyToggle
				) {
					const value =
						Number(item[key] * (conversionRate ?? 1)) >
						napsaCapLimit
							? napsaCapLimit
							: Number(item[key] * conversionRate);

					formattedItem[key] = formatNumber(
						value,
						data?.CompanyCurrencies
					);
				} else if (key === 'total' && usdCurrencyToggle) {
					const value =
						Number(
							item['napsaEmployerShare'] * (conversionRate ?? 1)
						) > napsaCapLimit
							? Number(item[key] * conversionRate) -
							  (Number(
									item['napsaEmployerShare'] *
										(conversionRate ?? 1)
							  ) -
									napsaCapLimit)
							: Number(item[key] * conversionRate);
					formattedItem[key] = formatNumber(
						value,
						data?.CompanyCurrencies
					);
				} else {
					const value = Number(item[key] * (conversionRate ?? 1));
					formattedItem[key] = formatNumber(
						value,
						data?.CompanyCurrencies
					);
				}
			}
			return formattedItem;
		});

		setDeductionData(formattedRowData);
	};

	const generateColumns = useMemo(() => {
		const flattenedColumns: any[] = [];

		// Add Employee column first
		flattenedColumns.push({
			title: 'Employees',
			dataIndex: 'Employee',
			key: 'Employee',
			className: 'bg-white',
			fixed: 'left',
			width: 300,
			render: (text: string, record: any) => {
				return (
					<span
						style={{
							cursor: 'default',
						}}
					>
						{text}
					</span>
				);
			},
		});

		// Process each column and flatten children
		columnsData.forEach((column: ColumnData) => {
			const processColumn = (col: ColumnData): any[] => {
				const { title, children, key } = col;

				if (!children || children.length === 0) {
					// Skip employer columns if not shown
					if (
						!showEmployer &&
						(key === 'napsaEmployerShare' ||
							key === 'nhimaEmployerShare')
					) {
						return [];
					}

					return [
						{
							title: getColumnTitle(key || title, title),
							dataIndex: key || title,
							key: key || title,
							width: '200px',
							className: 'bg-white',
							render: (value: any, record: any) => {
								const raw = record.__raw__;
								const isTotalRow = raw?.Employee === 'Total';

								// Don't show indicators for total row
								if (isTotalRow) {
									return <span>{value}</span>;
								}

								const employeeId = raw?.employeeId;

								// Check if we have payroll-specific data for this employee and field
								if (
									!payrollSpecificData ||
									!employeeId ||
									!payrollSpecificData[employeeId]
								) {
									return <span>{value}</span>;
								}

								const fieldName = key || title;
								const payrollSpecific =
									payrollSpecificData[employeeId][fieldName];

								// If no specific data for this field, return normal value
								if (!payrollSpecific?.isPayrollSpecific) {
									return <span>{value}</span>;
								}

								// Show tooltip for payroll-specific data
								const {
									reason,
									isThirdPartyLoan,
									currentBalance,
								} = payrollSpecific;

								const tooltipContent = (
									<div style={{ maxWidth: '250px' }}>
										{!isThirdPartyLoan && (
											<div
												style={{ marginBottom: '4px' }}
											>
												<span>Reason : </span>{' '}
												{!invalidText(reason)
													? reason
													: 'added for this payroll'}
											</div>
										)}

										{currentBalance && (
											<div>
												<span>Current Balance : </span>{' '}
												{formatNumber(
													currentBalance,
													data?.CompanyCurrencies
												)}
											</div>
										)}
									</div>
								);

								return (
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '6px',
										}}
									>
										<span>{value}</span>
										<Tooltip
											title={tooltipContent}
											placement="top"
										>
											<InfoCircleOutlined
												style={{
													color: '#584495',
													fontSize: '14px',
												}}
											/>
										</Tooltip>
									</div>
								);
							},
						},
					];
				}

				// Recursively process children
				const processedChildren: any[] = [];
				children.forEach((child) => {
					const childColumns = processColumn(child);
					processedChildren.push(...childColumns);
				});

				return processedChildren;
			};

			const processedColumns = processColumn(column);
			flattenedColumns.push(...processedColumns);
		});

		// Add Total column at the end
		flattenedColumns.push({
			title: 'Total',
			dataIndex: 'total',
			key: 'total',
			width: '150px',
			className: 'bg-white',
		});

		return flattenedColumns;
	}, [
		columnsData,
		showEmployer,
		payPeriodId,
		year,
		payrollSpecificData,
		data?.CompanyCurrencies,
	]);
	const handleChange = (
		value: string | number | null,
		name: string,
		required: boolean,
		regex?: RegExp | null
	) => {
		if (typeof value === 'number') {
			if (value <= 0) {
				return;
			}
		}

		OnChange(value, name);
	};

	const OnChange = (value: string | number | null, key: string) => {
		setDeductionDetails((prev: any) => {
			if (key === 'employeeId') {
				return {
					employeeId: value, // set new employee
					earningType: null, // reset earning type
					amount: null, // reset amount
				};
			}

			return {
				...prev,
				[key]: value,
			};
		});

		const checkFormError = validateFormData(
			{ [key]: value },
			{ ...formError }
		);
		setFormError(checkFormError);
	};

	useEffect(() => {
		formatData(rowData, deductionLimit);
	}, [usdCurrencyToggle]);

	const fetchDeductions = async (payrollId: string) => {
		try {
			let response;
			setIsDeductionLoading(true);
			setIsUniversalLoading(true);
			if (onlyView) {
				response = await viewPayrollApi.getAllEmployeeDeductions({
					payrollId: payrollId,
					page: page,
					pageSize: pageSize - 1,
				});
			} else {
				response = await payrollApi.getPayrollDeductions({
					payrollId: payrollId,
					page: page,
					pageSize: pageSize - 1,
				});
			}
			const payrollSpecific =
				response?.data?.data?.payrollSpecificData || {};
			setPayrollSpecificData(payrollSpecific);

			const {
				columns,
				rows,
				napsaCapLimit,
				totalRecords,
				payrollEmployeeOptions,
				isThirdPartyLoan,
				masterDeductionOptions,
			} = response.data.data;
			setDeductionLimit(napsaCapLimit ?? 1490.8);
			setTotalRecords(totalRecords);
			setEmployeeOptions(payrollEmployeeOptions);
			setIsThirdPartyLoan(isThirdPartyLoan);
			setDeductionsOptions(masterDeductionOptions);

			setColumnsData(columns);
			setRowData(rows);
			formatData(response.data.data.rows, napsaCapLimit);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching deductions';
			toastText(errorMessage, 'error');
		} finally {
			setIsDeductionLoading(false);
			setIsUniversalLoading(false);
		}
	};

	useEffect(() => {
		if (selectedPayrollId) {
			fetchDeductions(selectedPayrollId);
		}
	}, [selectedPayrollId, page, pageSize]);

	const myButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'secondary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			disabled: isLoading,
			isSubmit: false,
			onclick: () => {
				handleAddDeductionCancel();
			},
		},
		{
			text: 'Save',
			isLoading: isLoading,
			className: 'primary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: true,
			onclick: () => {
				handleSubmit();
			},
		},
	];

	const handleSubmit = async () => {
		let checkFormError = validateFormData(
			{ ...deductionDetails },
			{ ...formError }
		);

		checkFormError = {
			...checkFormError,
		};
		setFormError(checkFormError);
		if (hasFormError(checkFormError)) {
			return;
		}
		try {
			setIsLoading(true);
			
			const data = {
				employeeId: deductionDetails.employeeId,
				payrollId: selectedPayrollId,
				deductionId: deductionDetails.deductionType,
				amount: deductionDetails.amount,
				reason: deductionDetails.notes,
			};
			const apiResponse =
				await payrollApi.createEmployeeSpecificDeductions(data);
			const isUpdated = apiResponse.data.data.isUpdated ?? false;
			const employeeName = employeeOptions.find(
				(emp) => emp.value === data.employeeId
			).fullName;
			toastText(
				`Deduction successfully ${
					isUpdated ? 'updated' : 'added'
				} for ${employeeName}`,
				'success'
			);
			handleAddDeductionCancel();
			fetchDeductions(selectedPayrollId);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in adding new deduction';

			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="payroll-steps-table">
			<GlobalHeader
				headerData={headerData}
				toggleRequired={true}
				fetchAllData={() => fetchDeductions(selectedPayrollId)}
				isLoading={isDeductionLoading}
			/>

			{/* Controls Section */}
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '4px',
					fontSize: '14px',
				}}
			>
				{/* Left side - Statutory Components toggle */}
				<div>
					<span style={{ marginLeft: 10 }}>Statutory Components</span>
					<Switch
						checked={showEmployer}
						onChange={handleSwitchChange}
						style={{ marginLeft: 10 }}
					/>
					<span style={{ marginLeft: 10 }}>Show employers</span>
				</div>

				{/* Right side - Action buttons */}
				{!onlyView && (
					<div style={{ display: 'flex', gap: '8px' }}>
						<Button
							type="primary"
							onClick={showAddDeductionModal}
							disabled={isDeductionLoading}
						>
							Add Deduction
						</Button>
						{isThirdPartyLoan && (
							<Button
								type="primary"
								disabled={isDeductionLoading}
								onClick={showUploadLoanModal}
							>
								Upload Third Party Loan
							</Button>
						)}
					</div>
				)}
			</div>

			<Table
				columns={generateColumns}
				dataSource={deductionData}
				className="total-deduction-table"
				rowClassName={(record, index) =>
					index === deductionData.length - 1
						? 'sticky-row  color-purple font-bold'
						: ''
				}
				bordered
				scroll={{
					y: showSteps
						? 'calc(100vh - 455px)'
						: 'calc(100vh - 375px)',
				}}
				pagination={{
					total: totalRecords,
					current: page,
					pageSize: pageSize,
					showSizeChanger: false,
					pageSizeOptions: [10, 20, 50, 100, 200],
				}}
				loading={isDeductionLoading}
				onChange={tableChangeHandler}
			/>

			{isAddDeductionModalVisible && (
				<>
					<Modal
						open={isAddDeductionModalVisible}
						onOk={() => {}}
						onCancel={handleAddDeductionCancel}
						okText={'Save'}
						closable={false}
						width={650}
						centered
						footer={null}
						title={<h2>Add Deduction</h2>}
						rootClassName="addDeductionModalRoot"
					>
						<p className={styles['form-container-head-warning']}>
							<b>
								{' '}
								<sup>*</sup>
							</b>{' '}
							Indicated mandatory fields
						</p>

						<div
							style={{
								marginTop: '12px',
								padding: '12px 16px',
								backgroundColor: '#f6f4ff', // Light purple background
								borderRadius: '6px',
								fontSize: '14px',
								color: '#584495', // Main purple text color
								display: 'flex',
								alignItems: 'flex-start',
								gap: '4px',
							}}
						>
							<div>
								<strong style={{ color: '#584495' }}>
									Note:{' '}
								</strong>
								Any Deduction added will be applied to this
								payroll only.
							</div>
						</div>
						<Row
							className={styles['form-container-card']}
							gutter={50}
						>
							<Col
								span={24}
								className={`${styles['col']} margin-top-10`}
							>
								<SelectDropdown
									placeholder="Employee Name"
									options={employeeOptions}
									value={deductionDetails?.employeeId}
									onChange={(value: any) =>
										handleChange(value, 'employeeId', true)
									}
									size="large"
									required={true}
									helperText="Employee Name required"
									label="Employee Code | Employee Name"
									isError={formError.employeeId}
								/>
							</Col>{' '}
							<Col
								span={24}
								className={`${styles['col']} margin-top-10`}
							>
								<SelectDropdown
									placeholder="Deduction Type"
									options={
										deductionDetails?.employeeId
											? deductionsOptions[
													deductionDetails.employeeId
											  ] || []
											: []
									}
									value={deductionDetails?.deductionType}
									disabled={invalidText(
										deductionDetails?.employeeId
									)}
									onChange={(value: any) =>
										handleChange(
											value,
											'deductionType',
											true
										)
									}
									size="large"
									required={true}
									helperText="Deduction Type required"
									label="Deduction Type"
									isError={formError.deductionType}
								/>
							</Col>
							<Col
								span={24}
								className={`${styles['col']} margin-top-10`}
							>
								<InputNumberField
									name="amount"
									value={deductionDetails.amount}
									label="Amount"
									required={true}
									helperText="Amount must be greater than zero"
									onChange={(value) =>
										handleChange(value, 'amount', true)
									}
									// suffix={'ZMW'}
									isError={formError.amount}
								/>
							</Col>
							<Col
								span={24}
								className={`${styles['col']} margin-top-10`}
							>
								<TextareaFieldInput
									name="Reason"
									value={deductionDetails.notes}
									label="Reason"
									required={true}
									helperText="Reason is required"
									onChange={(value) =>
										handleChange(value, 'notes', true)
									}
									isError={formError.notes}
								/>
							</Col>
						</Row>
						<div className={styles['buttons']}>
							<Buttons buttons={myButtons} />
						</div>
					</Modal>
				</>
			)}
			{isUploadLoanModalVisible && isThirdPartyLoan && (
				<Modal
					open={isUploadLoanModalVisible}
					maskClosable={!showResults} // Prevent accidental close when showing results
					width={showResults ? '800px' : '600px'} // Wider for results
					footer={null}
					closable={false}
					onCancel={cancelUploadRecordModal}
					centered
				>
					{renderModalContent()}
				</Modal>
			)}
		</div>
	);
};

export default TotalDeductionTable;
